/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/require-tagless-components, no-unused-vars, warp-drive/no-legacy-request-patterns */
// Ember imports
import Component from '@ember/component';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
// External imports
import { classNameBindings, classNames } from '@ember-decorators/component';
import config from '@linc-technologies/linced-common/utils/config';
import moment from 'moment';
// Internal imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Options from '@linc-technologies/linced-common/enums/options';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import ReportTemplateTheme from '@linc-technologies/linced-common/enums/reports/report-template-theme';
import reportStudentName from '@linc-technologies/linced-common/utils/report-student-name';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import {
  byKeys,
  findBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { effortLabelsFor } from '@linc-technologies/linced-common/utils/effort-labels';
import { REVIEW_EDITABLE_TYPES } from '@linc-technologies/linced-common/utils/reporting';
import { flattenList } from '@linc-technologies/linced-common/utils/fields';
import { schoolLocalDate } from '@linc-technologies/linced-common/utils/school-date';

// Attendance analysis with nothing counted. The preview zero-fills with this,
// as the publish snapshot does, rather than showing the template's example
// figures.
function emptyAttendanceAnalysis() {
  return { counts: {}, lateCount: 0, partialAbsences: 0 };
}

// Guard the snapshot colour before interpolating it into htmlSafe: the value
// comes verbatim from the backend snapshot, so reject anything that isn't a
// plain CSS colour to avoid CSS injection via a tampered/malformed snapshot.
function isCssColour(value) {
  return /^(#[0-9a-fA-F]{3,8}|rgba?\([\d.,\s%]+\)|hsla?\([\d.,\s%]+\))$/.test(
    String(value).trim(),
  );
}

// Parses the sRGB channels out of a hex or rgb()/rgba() colour. Returns null
// for anything else (hsl, named colours), which callers treat as "unknown".
function parseRgb(colour) {
  let value = String(colour).trim();

  let hex = value.match(/^#([0-9a-fA-F]{3,8})$/);
  if (hex) {
    let digits = hex[1];
    // Expand the #rgb / #rgba shorthand to two digits per channel.
    if (digits.length === 3 || digits.length === 4) {
      digits = digits
        .split('')
        .map((d) => d + d)
        .join('');
    }
    if (digits.length !== 6 && digits.length !== 8) return null;
    return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
  }

  let rgb = value.match(/^rgba?\(([^)]+)\)$/);
  if (rgb) {
    let parts = rgb[1]
      .split(/[,/\s]+/)
      .filter(Boolean)
      .slice(0, 3);
    if (parts.length !== 3) return null;
    let channels = parts.map((part) =>
      part.endsWith('%')
        ? Math.round((parseFloat(part) / 100) * 255)
        : parseFloat(part),
    );
    return channels.some((c) => !isFinite(c)) ? null : channels;
  }

  return null;
}

// WCAG 2.x relative luminance (w3.org/TR/WCAG21/#dfn-relative-luminance).
function relativeLuminance([r, g, b]) {
  const channel = (value) => {
    let c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// Picks whichever of black/white contrasts better against the given
// background. School-configured progress colours span the full spectrum, and
// bright ones (yellows especially) leave white text unreadable. Falls back to
// white when the colour can't be parsed, preserving the previous behaviour.
function readableTextColour(background) {
  let rgb = parseRgb(background);
  if (!rgb) return '#fff';

  // Contrast ratio against white is (1.05 / (L + 0.05)); against black it is
  // ((L + 0.05) / 0.05). They cross at L = sqrt(1.05 * 0.05) - 0.05 ≈ 0.179.
  let luminance = relativeLuminance(rgb);
  return luminance > Math.sqrt(1.05 * 0.05) - 0.05
    ? 'rgba(0, 0, 0, 0.87)'
    : '#fff';
}

// Mirrors the field-value helper: dispatch on datatype and map option codes
// to their labels via flattenList for STRING/LIST/INTEGER fields.
// fieldValue is an ObjectProxy wrapping a dynamic-field/value fragment (see
// DynamicFields mixin's `fields` computed) -- .get() is required over plain
// access, but isn't sufficient on its own: if the underlying fieldsArray
// hasn't hydrated yet, the wrapped fragment can still be a proxy mid-load,
// and even .get('value') on that throws the same "special kind of Ember
// object (a proxy)" assertion. Caught defensively rather than trying to
// pin down every intermediate state that can trigger it.
function displayFieldValue(fieldValue, field) {
  if (isEmpty(fieldValue)) return '';
  try {
    let value = fieldValue.get('value');
    if (isEmpty(value)) return '';
    switch (fieldValue.get('datatype')) {
      case DataType.STRING:
        return field ? flattenList([value], field) : value;
      case DataType.LIST:
        return flattenList(value, field);
      case DataType.BOOLEAN:
        return value ? 'Yes' : 'No';
      case DataType.INTEGER:
        return field ? flattenList([value], field) : value;
      default:
        return value;
    }
  } catch {
    return '';
  }
}

@classNames('report-builder__preview')
@classNameBindings('themeClass')
export default class ReportView extends Component {
  @service
  colours;

  @service
  dates;

  @service
  fetch;

  @service
  media;

  @service
  options;

  @service
  session;

  @service
  store;

  ReportTemplateSectionType = ReportTemplateSectionType;
  ProgressionJudgement = ProgressionJudgement;
  Jurisdiction = Jurisdiction;

  // Required
  @tracked reportTemplate = null;

  @tracked student = null;

  // Optional
  @tracked studentReport = null;
  // Set by the template builder, where no report exists yet so there is no
  // period to name. The period badge shows a placeholder instead.
  isTemplatePreview = false;
  // Builder only: clicking the placeholder period badge jumps to the template's
  // period setup.
  onSelectPeriod = null;

  onSelectComponent = null;
  onRegisterCapture = null;
  onRegisterVerifyCaptured = null;
  onRegisterRefresh = null;
  @tracked reviewProgressionRecords;
  @tracked reviewProgressionRecordsLoading = false;
  @tracked school = null;
  @tracked editableTypes = null;

  get editableComponentTypes() {
    if (this.editableTypes !== null) {
      return this.editableTypes;
    }
    return this.studentReport ? REVIEW_EDITABLE_TYPES : null;
  }

  // Student data state (self-loaded)
  @tracked
  _studentProgressions = null;

  @tracked
  assessmentGrades = null;
  @tracked
  assessmentGradesLoading = false;
  @tracked
  _lastStudentDataId = null;
  @tracked
  _dataReadyResolve = null;
  @tracked _attendanceAnalysis = null;
  // First and last day (YYYY-MM-DD) the live attendance totals cover.
  @tracked _attendanceDateFrom = null;
  @tracked _attendanceDateTo = null;
  @tracked
  _attendanceLoading = false;
  _attendanceTerms = null;
  @tracked
  _snapshotMediaLoading = false;
  _snapshotMediaGeneration = 0;

  didInsertElement() {
    super.didInsertElement(...arguments);
    if (this.onRegisterCapture) {
      this.onRegisterCapture(this.captureVisuals.bind(this));
    }
    if (this.onRegisterVerifyCaptured) {
      this.onRegisterVerifyCaptured(this.verifyCapturedVisuals.bind(this));
    }
    if (this.onRegisterRefresh) {
      this.onRegisterRefresh(this.refreshStudentData.bind(this));
    }
    if (this.onRegisterWaitForReady) {
      this.onRegisterWaitForReady(this._waitForDataReady.bind(this));
    }
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this._loadStudentData();
    this._ensureSubjectsLoaded();
    this._ensureDetailFieldsLoaded();
    this._loadHeaderMedia();
  }

  init() {
    super.init(...arguments);
    // Synthetic resource holders for the avatar / logo images, for both the
    // snapshot path and the live path. The media service writes the
    // resolved blob URL onto an `image` / `logo` property of whatever
    // resource we hand it (and uses the resource's `id` for the Gravatar
    // fallback). We hand it standalone Ember objects rather than the live
    // student / school records directly (those models no longer carry
    // image/logo resolution themselves -- see AvatarImage for the
    // component-level equivalent, which this component can't use directly
    // since it needs a plain url string here, not a rendered <img>) and
    // read .image / .logo off the holders via previewAvatarSrc /
    // previewLogoSrc.
    this._snapshotAvatarHolder = EmberObject.create({ image: '' });
    this._snapshotLogoHolder = EmberObject.create({ logo: '' });
    this._lastSnapshotAvatarMediaId = null;
    this._lastSnapshotLogoMediaId = null;
    this._liveAvatarHolder = EmberObject.create({ image: '' });
    this._liveLogoHolder = EmberObject.create({ logo: '' });
    this._lastLiveAvatarMediaId = null;
    this._lastLiveLogoMediaId = null;
  }

  // Triggers the media service to resolve avatar/logo mediaIds onto the
  // synthetic holders, for whichever path (snapshot or live) is active.
  // Called from didReceiveAttrs; idempotent per holder — only refetches when
  // that holder's mediaId or underlying person/school identity changes.
  _loadHeaderMedia() {
    let snap = this.headerSnapshot;
    let pending = [];

    if (snap) {
      let avatarMediaId =
        snap.studentNameAvatar && snap.studentNameAvatar.avatarMediaId;
      let logoMediaId = snap.schoolNameLogo && snap.schoolNameLogo.logoMediaId;

      if (avatarMediaId && avatarMediaId !== this._lastSnapshotAvatarMediaId) {
        this._lastSnapshotAvatarMediaId = avatarMediaId;
        // Use the student's person id as the Gravatar fallback key so a
        // missing/deleted media record still degrades to the same
        // identicon the live path would show.
        this._snapshotAvatarHolder.set(
          'id',
          this.student && this.student.get('id'),
        );
        pending.push(
          this.media.onResourceLoadOrUpdate(
            this._snapshotAvatarHolder,
            avatarMediaId,
            'image',
            true,
          ),
        );
      }
      if (logoMediaId && logoMediaId !== this._lastSnapshotLogoMediaId) {
        this._lastSnapshotLogoMediaId = logoMediaId;
        this._snapshotLogoHolder.set(
          'id',
          this.schoolRecord && this.schoolRecord.get('id'),
        );
        pending.push(
          this.media.onResourceLoadOrUpdate(
            this._snapshotLogoHolder,
            logoMediaId,
            'logo',
            true,
          ),
        );
      }
    } else {
      let student = this.student;
      let avatarMediaId = student && student.get('mediaId');
      if (avatarMediaId !== this._lastLiveAvatarMediaId) {
        this._lastLiveAvatarMediaId = avatarMediaId;
        this._liveAvatarHolder.set('id', student && student.get('id'));
        pending.push(
          this.media.onResourceLoadOrUpdate(
            this._liveAvatarHolder,
            avatarMediaId,
            'image',
            true,
          ),
        );
      }

      let schoolRecord = this.schoolRecord;
      let logoMediaId = schoolRecord && schoolRecord.get('mediaId');
      if (logoMediaId !== this._lastLiveLogoMediaId) {
        this._lastLiveLogoMediaId = logoMediaId;
        this._liveLogoHolder.set('id', schoolRecord && schoolRecord.get('id'));
        pending.push(
          this.media.onResourceLoadOrUpdate(
            this._liveLogoHolder,
            logoMediaId,
            'logo',
            true,
          ),
        );
      }
    }

    if (!pending.length) {
      this._cancelSnapshotMediaLoading();
      return;
    }

    // Gate _waitForDataReady on these fetches. onResourceLoadOrUpdate first
    // sets a synchronous cross-origin Gravatar fallback URL, then awaits the
    // real same-origin blob URL. A batch renderer (bulk PDF / visual capture)
    // rasterises after _waitForDataReady resolves, and html2canvas can't draw
    // the cross-origin fallback — only the blob. Without waiting, the snapshot
    // avatar / logo capture blank.
    this._snapshotMediaLoading = true;
    // Guard against a stale load resolving after a newer one has started (e.g.
    // the student changes mid-bulk-PDF run). Only the latest generation may
    // clear the loading flag and resolve data-ready.
    let gen = ++this._snapshotMediaGeneration;
    Promise.all(pending)
      .catch(() => null)
      .then(() => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        if (this._snapshotMediaGeneration !== gen) {
          return;
        }
        this._snapshotMediaLoading = false;
        this._tryResolveDataReady();
      });
  }

  // No snapshot media is needed for the current student. Bump the generation
  // so a previous student's still-in-flight all(pending) can no longer clear
  // the loading flag, then clear it ourselves — otherwise _waitForDataReady
  // would stay gated on a media load that was never relevant to this student.
  _cancelSnapshotMediaLoading() {
    this._snapshotMediaGeneration++;
    if (this._snapshotMediaLoading) {
      this._snapshotMediaLoading = false;
      this._tryResolveDataReady();
    }
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    if (this.onRegisterCapture) {
      this.onRegisterCapture(null);
    }
    if (this.onRegisterVerifyCaptured) {
      this.onRegisterVerifyCaptured(null);
    }
    if (this.onRegisterRefresh) {
      this.onRegisterRefresh(null);
    }
    if (this.onRegisterWaitForReady) {
      this.onRegisterWaitForReady(null);
    }
    // Resolve any pending wait so callers don't hang if the component is torn down
    const resolve = this._dataReadyResolve;
    if (resolve) {
      this._dataReadyResolve = null;
      resolve();
    }
  }

  async captureVisuals() {
    let cards = this.element.querySelectorAll('[data-section-id]');
    let saves = [];
    cards.forEach((card) => {
      let sectionId = card.dataset.sectionId;
      let studentSection = this.studentSectionMap[sectionId];
      if (!studentSection) {
        return;
      }

      let strandSvg = card.querySelector('svg.strand-slider-visual');
      if (strandSvg) {
        studentSection.set(
          'strandSliderVisual',
          new XMLSerializer().serializeToString(strandSvg),
        );
      }

      // Progress-visual renders the dark title bar ("Student · Period ·
      // Subject") and the chart side-by-side inside its component root, so
      // capturing only the inner SVG drops the header. Take the
      // component-root outerHTML instead — it includes both the header div
      // and the chart SVG.
      let progressVisual = card.querySelector('.report-builder__preview-pv');
      if (progressVisual) {
        studentSection.set('progressVisual', progressVisual.outerHTML);
      }

      // Strand-visual table variants (table-effort / table-no-effort) render
      // as HTML rather than SVG, so we capture outerHTML rather than serialise
      // through XMLSerializer. The wrapper div is the ReportStrandTable
      // component root; its descendants include the styled <table>.
      let strandTable = card.querySelector(
        '.report-builder__preview-strands-table-wrap',
      );
      if (strandTable) {
        studentSection.set('strandTableVisual', strandTable.outerHTML);
      }

      if (strandSvg || progressVisual || strandTable) {
        saves.push(studentSection.save());
      }
    });
    await Promise.all(saves);
  }

  // Reports visuals that rendered in the DOM but did NOT get serialised onto
  // their section — i.e. the chart element is present now, yet its field is
  // still empty. That gap means captureVisuals() ran before the chart had
  // painted (a settle/timing race). Callers can retry the settle + capture.
  //
  // We only flag visuals whose element is present: an ABSENT element means
  // "nothing to draw" (non-trackable subject, no data, N/A strand), which is a
  // legitimately empty field, not a miss. Selectors mirror captureVisuals()
  // exactly — keep them in sync. Returns [{ sectionId, visual }].
  verifyCapturedVisuals() {
    let missing = [];
    let cards = this.element.querySelectorAll('[data-section-id]');
    cards.forEach((card) => {
      let sectionId = card.dataset.sectionId;
      let studentSection = this.studentSectionMap[sectionId];
      if (!studentSection) {
        return;
      }
      if (
        card.querySelector('svg.strand-slider-visual') &&
        !studentSection.get('strandSliderVisual')
      ) {
        missing.push({ sectionId, visual: 'strandSliderVisual' });
      }
      if (
        card.querySelector('.report-builder__preview-pv') &&
        !studentSection.get('progressVisual')
      ) {
        missing.push({ sectionId, visual: 'progressVisual' });
      }
      if (
        card.querySelector('.report-builder__preview-strands-table-wrap') &&
        !studentSection.get('strandTableVisual')
      ) {
        missing.push({ sectionId, visual: 'strandTableVisual' });
      }
    });
    return missing;
  }

  _isComponentClickable(component) {
    let types = this.editableComponentTypes;
    if (!types) {
      return true;
    }
    let compType = component.type || (component.get && component.get('type'));
    return types.includes(compType);
  }

  @action
  previewSelectPeriod() {
    if (this.onSelectPeriod) {
      this.onSelectPeriod();
    }
  }

  @action
  previewSelectComponent(section, component) {
    if (!this._isComponentClickable(component)) {
      return;
    }
    if (this.onSelectComponent) {
      this.onSelectComponent(section, component);
    }
  }

  @action
  getChipColorStyle(judgement, fallback, snapshotColour) {
    // Snapshot path: backend has already resolved the chip colour via
    // colours.progress(school, value) at publish time. Use it verbatim
    // so a post-publish change to the school's colour options doesn't
    // restyle frozen reports.
    if (snapshotColour && isCssColour(snapshotColour)) {
      return htmlSafe(
        `background-color: ${snapshotColour}; color: ${readableTextColour(
          snapshotColour,
        )};`,
      );
    }
    let numeric =
      judgement != null && judgement !== ''
        ? parseInt(judgement, 10)
        : fallback;
    if (numeric == null) {
      return htmlSafe('');
    }
    let colour = this.colours.progress(this.schoolRecord, numeric);
    if (!colour) {
      return htmlSafe('');
    }
    return htmlSafe(
      `background-color: ${colour}; color: ${readableTextColour(colour)};`,
    );
  }

  // Snapshot attendance counts (publishedSnapshot.attendanceData) are raw
  // JSON values, so coerce to a finite non-negative number before it reaches
  // the html-safe flex style — a crafted string would otherwise inject CSS,
  // the same class of issue guarded against for chip colours via isCssColour.
  @action
  getAttendanceFlexStyle(value) {
    let numeric = Number(value);
    if (!isFinite(numeric) || numeric < 0) {
      return htmlSafe('');
    }
    return htmlSafe(`flex: ${numeric}`);
  }

  @computed('session.school', 'school')
  get schoolRecord() {
    return this.school || this.session.school;
  }

  @computed('schoolRecord.jurisdiction')
  get jurisdiction() {
    return this.get('schoolRecord.jurisdiction');
  }

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  // Mid-range effort label shown in template/preview mode (no student data).
  @computed('jurisdiction')
  get previewEffortLabel() {
    let labels = effortLabelsFor(this.jurisdiction);
    let middle = labels[Math.floor(labels.length / 2)];
    return middle ? middle.label : '';
  }

  @computed('schoolRecord', 'jurisdiction')
  get periods() {
    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      return 'Days';
    }
    return this.options.schoolGet(
      this.schoolRecord,
      Options.ATTENDANCE_PERIODS_LABEL,
    );
  }

  // _attendanceTotals builds the attendance totals per jurisdiction rules
  @computed(
    '_attendanceAnalysis',
    '_attendanceDateFrom',
    '_attendanceDateTo',
    'jurisdiction',
  )
  get _attendanceTotals() {
    let analysis = this._attendanceAnalysis;
    let counts = analysis && analysis.counts;
    if (!counts) {
      return null;
    }
    let isNsw = this.jurisdiction === Jurisdiction.AU_NSW;
    let divisor = isNsw ? 2 : 1;
    let present = (counts.P || 0) / divisor;
    let justified = (counts.J || 0) / divisor;
    let unjustified = (counts.U || 0) / divisor;
    let late = isNsw ? 0 : analysis.lateCount || 0;
    let total = present + justified + unjustified;
    let absentHalfDays = (counts.J || 0) + (counts.U || 0);
    return {
      present,
      late,
      justified,
      unjustified,
      total,
      absentWholeDays: Math.floor(absentHalfDays / 2),
      partialAbsences: analysis.partialAbsences,
      presentPct: total > 0 ? ((present / total) * 100).toFixed(1) : '0.0',
      dateFrom: this._attendanceDateFrom,
      dateTo: this._attendanceDateTo,
    };
  }

  @computed('reportTemplate.theme')
  get themeClass() {
    let themeValue = this.get('reportTemplate.theme');
    let entry = ReportTemplateTheme.values.find((v) => v.value === themeValue);
    let label = entry && entry.label !== 'Unspecified' ? entry.label : 'Teal';
    return `report-builder__preview--${label.toLowerCase()}`;
  }

  @computed('reportTemplate.theme')
  get themeLabel() {
    let themeValue = this.get('reportTemplate.theme');
    let entry = ReportTemplateTheme.values.find((v) => v.value === themeValue);
    let label = entry && entry.label !== 'Unspecified' ? entry.label : 'Teal';
    return label.toLowerCase();
  }

  @computed
  get _allTemplateSections() {
    return this.store.peekAll('reports/report-template-section');
  }

  @computed
  get _allSubjects() {
    return this.store.peekAll('goals/subject');
  }

  @computed('_allSubjects.[]')
  get subjectsById() {
    let map = {};
    (this._allSubjects || []).forEach((s) => (map[s.get('id')] = s));
    return map;
  }

  @computed(
    'reportTemplate',
    '_allTemplateSections.@each.{sequence,isDeleted,reportTemplateId}',
  )
  get resolvedSections() {
    let template = this.reportTemplate;
    if (!template) {
      return [];
    }
    let templateId = template.get('id');
    return this._allTemplateSections
      .filter((s) => {
        if (s.get('isDeleted') || s.get('isDestroyed')) {
          return false;
        }
        let ref = s.belongsTo('reportTemplateId');
        return ref.value() === template || ref.id() === templateId;
      })
      .sort(byKeys('sequence'));
  }

  @computed('reportTemplate.name')
  get resolvedTemplateName() {
    return this.get('reportTemplate.name') || '';
  }

  @computed('reportTemplate.periods.[]', 'studentReport.periodIndex')
  get resolvedPeriodLabel() {
    let periods = this.get('reportTemplate.periods');
    if (!periods || !periods.length) {
      return '';
    }
    let sorted = sortBy(periods, 'month');
    let periodIndex = this.get('studentReport.periodIndex');
    if (
      periodIndex != null &&
      periodIndex >= 0 &&
      periodIndex < sorted.length
    ) {
      return sorted[periodIndex].get('label') || '';
    }
    // Fallback for preview/builder mode where studentReport has no periodIndex
    let currentMonth = new Date().getMonth() + 1;
    let current = null;
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].get('month') <= currentMonth) {
        current = sorted[i];
      }
    }
    return (current || sorted[0]).get('label') || '';
  }

  @computed('resolvedSections.length')
  get hasSections() {
    return this.get('resolvedSections.length') > 0;
  }

  @computed('resolvedSections.@each.sectionType')
  get headerSection() {
    return findBy(
      this.resolvedSections || [],
      'sectionType',
      ReportTemplateSectionType.HEADER,
    );
  }

  @computed('resolvedSections.@each.sectionType')
  get nonHeaderSections() {
    return rejectBy(
      this.resolvedSections || [],
      'sectionType',
      ReportTemplateSectionType.HEADER,
    );
  }

  COMPONENT_FIELD_MAP = Object.freeze({
    'teacher-comment': 'narrative',
    'next-steps': 'nextStepsDisplay',
    'help-at-home': 'homeHelp',
  });

  @computed
  get allProgressions() {
    return this.store.peekAll('milestones/progression');
  }

  @computed(
    'reviewProgressionRecords',
    'reviewProgressionRecordsLoading',
    '_studentProgressions.@each.judgement',
    'studentProgressions.[]',
  )
  get resolvedProgressions() {
    // While per-subject records are loading, show the broader student set
    if (this.reviewProgressionRecordsLoading) {
      return this._studentProgressions || this.studentProgressions;
    }
    // Per-subject review records (set when editing a progress-visual)
    let review = this.reviewProgressionRecords;
    if (review !== undefined && review !== null) {
      return review;
    }
    // Self-loaded progressions for review/write mode
    if (this._studentProgressions) {
      return this._studentProgressions;
    }
    // Template mode fallback
    return this.studentProgressions;
  }

  @computed('student', 'allProgressions.[]')
  get studentProgressions() {
    let student = this.student;
    if (!student) {
      return [];
    }
    let studentId = student.get('personId') || student.get('id');
    return this.allProgressions.filter((progression) => {
      return progression.get('person_id') === studentId;
    });
  }

  // Resolves section ID → subject, parsing configuration whether it's a string
  // or already-parsed object (the API can return either form).
  @computed('nonHeaderSections.@each.configuration', '_allSubjects.[]')
  get sectionSubjectMap() {
    let map = {};
    (this.nonHeaderSections || []).forEach((section) => {
      let config = section.get('configuration');
      if (!config) {
        return;
      }
      let parsed = typeof config === 'string' ? JSON.parse(config) : config;
      let subjectId = parsed && parsed.subjectId;
      if (subjectId) {
        map[section.get('id')] = this.store.peekRecord(
          'goals/subject',
          subjectId,
        );
      }
    });
    return map;
  }

  @computed
  get _allStudentReportSections() {
    return this.store.peekAll('reports/student-report-section');
  }

  @computed(
    'studentReport',
    '_allStudentReportSections.@each.{narrative,nextSteps,nextStepsGoals,homeHelp}',
  )
  get studentSectionMap() {
    let report = this.studentReport;
    if (!report) {
      return {};
    }
    let reportId = report.get('id');
    let map = {};
    this._allStudentReportSections.forEach((section) => {
      let sectionReportId = section.belongsTo('studentReportId').id();
      if (sectionReportId === reportId) {
        let templateSectionId = section
          .belongsTo('reportTemplateSectionId')
          .id();
        map[templateSectionId] = section;
      }
    });
    return map;
  }

  // True when the report is in the PUBLISHED state. Per the snapshot contract
  // (docs/reporting/publish-snapshot.md), the renderer reads from each
  // section's publishedSnapshot when this is true AND that section's snapshot
  // is non-null; pre-13205 published reports (snapshot null) keep falling
  // through to the live-data path per-section.
  @computed('studentReport.state')
  get isPublished() {
    return this.get('studentReport.state') === StudentReportState.PUBLISHED;
  }

  // The header section's publishedSnapshot if the report is published and the
  // backend has emitted one; null otherwise. Header reads (school name/logo,
  // student name/avatar, student-detail field values) branch onto this when
  // present.
  @computed('isPublished', 'headerSection', 'studentSectionMap')
  get headerSnapshot() {
    if (!this.isPublished) return null;
    let header = this.headerSection;
    if (!header) return null;
    let studentSection = this.studentSectionMap[header.get('id')];
    if (!studentSection) return null;
    return studentSection.get('publishedSnapshot') || null;
  }

  _parseConfig(section) {
    let config = section.get('configuration');
    if (!config) {
      return { components: [] };
    }
    if (typeof config === 'string') {
      try {
        return JSON.parse(config);
      } catch (e) {
        return { components: [] };
      }
    }
    return config;
  }

  // Returns a promise that resolves immediately if all async data is loaded,
  // or waits until both assessment grades and attendance have settled.
  _waitForDataReady() {
    if (
      !this.assessmentGradesLoading &&
      !this._attendanceLoading &&
      !this._snapshotMediaLoading
    ) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._dataReadyResolve = resolve;
    });
  }

  _tryResolveDataReady() {
    if (
      this.assessmentGradesLoading ||
      this._attendanceLoading ||
      this._snapshotMediaLoading
    )
      return;
    const resolve = this._dataReadyResolve;
    if (resolve) {
      this._dataReadyResolve = null;
      resolve();
    }
  }

  _loadStudentData() {
    let student = this.student;
    let studentReport = this.studentReport;
    if (!studentReport || !student) {
      this._studentProgressions = null;
      this.assessmentGrades = null;
      this._lastStudentDataId = null;
      this._attendanceAnalysis = null;
      this._attendanceLoading = false;
      return;
    }
    // Re-fetch when either the student changes or the report's state
    // changes — a void / send-back transitions PUBLISHED → DRAFT/REVIEW
    // and we need to swap from snapshot rendering back to live data
    // (the snapshot-mode gates in the fetch helpers skip the live load
    // while published, so without re-firing here the live values stay
    // null after the state transition).
    let cacheKey = `${student.get('id')}|${studentReport.get('state')}`;
    if (cacheKey === this._lastStudentDataId) {
      return;
    }
    this._lastStudentDataId = cacheKey;

    this._fetchStudentData(student);
    this._fetchAttendanceData(student, studentReport);
  }

  _fetchStudentData(student) {
    let studentId = student.get('personId') || student.get('id');
    // Snapshot-mode short-circuit: progressions feed the progress-visual
    // chart, which is rendered from the captured SVG when the report is
    // published-with-snapshot, so the query produces no visible data.
    // Skip both it and any subsequent assessment loads.
    let snapshotMode = this.isPublished && this._hasAnySnapshot();
    if (snapshotMode) {
      // Same race as the attendance gate: clear any stale loading flag a
      // prior inflight fetch left set so _waitForDataReady doesn't block on
      // an abandoned promise.
      this.assessmentGradesLoading = false;
      this._tryResolveDataReady();
      return;
    }
    // Skip the assessment grade / module / assessment fetches when the
    // report is published and any section already has an assessmentData
    // snapshot — the template reads it straight from the snapshot via
    // the assessment-groups helper's short-circuit.
    let hasAssessmentModules =
      this._collectAssessmentModuleIds().size > 0 &&
      !(this.isPublished && this._hasAssessmentSnapshot());
    let promises = [
      this.store.query('milestones/progression', {
        personId: studentId,
        latest: true,
        progressionType: ProgressionType.PROGRESSION,
        schoolId: student.get('school_id'),
      }),
    ];

    let gradesQuery = null;
    if (hasAssessmentModules) {
      // If grades and their schedules are already in the store for this student
      // (e.g. preloaded by bulk-pdf-download before rendering), display them
      // immediately and skip the loading placeholder — the background fetch will
      // still update them. We require the schedules to be warm too: without them
      // getGradeForDisplay can't resolve each grade's award method synchronously,
      // so every label would render as "N/A" in a PDF captured off this render.
      const cachedGrades = this.store
        .peekAll('assessments/grade')
        .filter((g) => g.get('person_id') === studentId);
      const gradeSchedulesWarm =
        this.store.peekAll('assessments/grade-schedule').length > 0;
      if (cachedGrades.length > 0 && gradeSchedulesWarm) {
        this.assessmentGrades = cachedGrades.slice();
      } else {
        this.assessmentGradesLoading = true;
      }
      let usesCurrentYear = this._usesCurrentYearGrades();
      if (usesCurrentYear) {
        gradesQuery = this.store.query('assessments/grade', {
          personId: studentId,
        });
      } else {
        gradesQuery = this.store.query('assessments/grade', {
          personId: studentId,
          latest: true,
        });
      }
      promises.push(
        this.store.findAll('assessments/assessment'),
        this.store.findAll('assessments/module'),
        gradesQuery,
        // Preload grade schedules so getGradeForDisplay can resolve each
        // grade's award method / label synchronously when the assessment
        // cells render. Without this, SELECT / MULTI_SELECT / ICON_SELECT
        // grades render as "N/A" (HERO-13256).
        this.store.findAll('assessments/grade-schedule'),
      );
    }

    Promise.all(promises)
      .then(async (results) => {
        // Resolve grades from the named query rather than a positional index
        // so reordering the promises above can't silently grab the wrong one.
        // The query gives a plain Promise, so await it to get the records.
        const grades = await gradesQuery;
        if (this.isDestroying || this.isDestroyed || this.student !== student) {
          return;
        }
        this._studentProgressions = results[0].slice();
        if (hasAssessmentModules) {
          this.assessmentGrades = grades.slice();
          this.assessmentGradesLoading = false;
          this._tryResolveDataReady();
        }
      })
      .catch(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this._studentProgressions = null;
        this.assessmentGrades = null;
        this.assessmentGradesLoading = false;
        this._tryResolveDataReady();
      });
  }

  refreshStudentData() {
    let student = this.student;
    if (!student || !this.studentReport) return;
    this._fetchStudentData(student);
  }

  // True when at least one attendance section in this report carries a
  // publishedSnapshot.attendanceData blob. Used to gate the live attendance
  // fetch off for published reports — no point hitting the network if the
  // template will read from the snapshot anyway.
  _hasAttendanceSnapshot() {
    let map = this.studentSectionMap;
    if (!map) return false;
    let sections = this.nonHeaderSections || [];
    for (let i = 0; i < sections.length; i++) {
      let section = sections[i];
      if (section.get('sectionType') !== ReportTemplateSectionType.ATTENDANCE) {
        continue;
      }
      let studentSection = map[section.get('id')];
      if (!studentSection) continue;
      let snap = studentSection.get('publishedSnapshot');
      if (snap && snap.attendanceData) return true;
    }
    return false;
  }

  // True when at least one section in this report carries any publishedSnapshot
  // blob (not just a specific slot). Used as the umbrella gate for live-data
  // loads whose results would be ignored anyway when the renderer is on the
  // snapshot path — progressions (the chart is captured as SVG), subjects
  // (chips read from snapshot.effort/progressDescriptor, strand-visual reads
  // from captured markup), peopleSchema fields (studentDetail reads from
  // snapshot). Pre-HERO-13205 published reports return false here and keep
  // the live-load path intact.
  _hasAnySnapshot() {
    let map = this.studentSectionMap;
    if (!map) return false;
    let keys = Object.keys(map);
    for (let i = 0; i < keys.length; i++) {
      let studentSection = map[keys[i]];
      if (!studentSection) continue;
      if (studentSection.get('publishedSnapshot')) return true;
    }
    return false;
  }

  // True when at least one section in this report carries a
  // publishedSnapshot.assessmentData blob. Used to gate the live assessment
  // grade / module / assessment fetches off — the assessment-groups helper
  // short-circuits to the snapshot anyway.
  _hasAssessmentSnapshot() {
    let map = this.studentSectionMap;
    if (!map) return false;
    let keys = Object.keys(map);
    for (let i = 0; i < keys.length; i++) {
      let studentSection = map[keys[i]];
      if (!studentSection) continue;
      let snap = studentSection.get('publishedSnapshot');
      if (snap && snap.assessmentData) return true;
    }
    return false;
  }

  _fetchAttendanceData(student, studentReport) {
    let studentId = student.get('personId') || student.get('id');
    let schoolId = student.get('school_id');
    let year = studentReport.get('year');
    let jurisdiction = this.jurisdiction;
    if (!year || !studentId || !schoolId) return;
    if (![Jurisdiction.NZ_MOE, Jurisdiction.AU_NSW].includes(jurisdiction))
      return;
    // Skip the live attendance fetch entirely when the report is published
    // and any attendance section already has a publishedSnapshot.attendanceData
    // — the template reads from that snapshot directly. Avoids a wasted
    // round-trip and any data drift from late-entered attendance records.
    // Clear any stale loading flag a prior inflight fetch left set (e.g. a
    // PUBLISHED transition re-fires _loadStudentData) so _waitForDataReady
    // isn't blocked waiting on a promise we've abandoned.
    if (this.isPublished && this._hasAttendanceSnapshot()) {
      this._attendanceLoading = false;
      this._tryResolveDataReady();
      return;
    }

    this._attendanceLoading = true;

    this._fetchAttendanceAnalysis(student, studentId, year);
  }

  _fetchAttendanceAnalysis(student, studentId, year) {
    let bounds =
      this.dates && this.dates.halfyearDatesForAcademicYear(year.toString());
    if (!bounds) {
      this._attendanceAnalysis = null;
      this._attendanceLoading = false;
      this._tryResolveDataReady();
      return;
    }
    let from = bounds.startDate.format('YYYY-MM-DD');
    let to = bounds.endDate.format('YYYY-MM-DD');

    // Cap the end bound at the day before today, in the school's timezone, so
    // the preview matches what publishing freezes: attendance to date rather
    // than the remainder of the academic year, without a part-marked today or
    // codes already entered for later in the day. For past years the year end
    // is still earlier than yesterday, so keep whichever is earlier.
    let yesterday = moment(schoolLocalDate(this.jurisdiction))
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    let dateTo = yesterday < to ? yesterday : to;
    // On the first day of the academic year there is nothing to count yet.
    if (dateTo < from) {
      this._attendanceAnalysis = emptyAttendanceAnalysis();
      this._attendanceDateFrom = null;
      this._attendanceDateTo = null;
      this._attendanceLoading = false;
      this._tryResolveDataReady();
      return;
    }

    let schoolId = student.get('school_id');
    let personIds = encodeURIComponent(JSON.stringify([studentId]));
    let url = `${config.APP.apiHost}/attendance/v4/daycodes?schoolId=${schoolId}&personIds=${personIds}&dateFrom=${from}&dateTo=${dateTo}&analysis=true`;

    this.fetch
      .request(url)
      .then((resp) => {
        if (this.isDestroying || this.isDestroyed || this.student !== student)
          return;
        let dayCodes = (resp && resp.dayCodes) || [];
        let analysis =
          dayCodes.find((d) => d && d.personId === studentId) ||
          emptyAttendanceAnalysis();
        this._attendanceAnalysis = analysis;
        this._attendanceDateFrom = from;
        this._attendanceDateTo = dateTo;
        this._attendanceLoading = false;
        this._tryResolveDataReady();
      })
      .catch(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this._attendanceAnalysis = null;
        this._attendanceLoading = false;
        this._tryResolveDataReady();
      });
  }

  // True when the given section carries its own publishedSnapshot blob.
  // Used for per-section snapshot gating so a partially-snapshotted report
  // (e.g. header snapshotted but content sections not) still loads live data
  // for the sections that lack a snapshot.
  _sectionHasSnapshot(section) {
    let map = this.studentSectionMap;
    if (!map || !section) return false;
    let studentSection = map[section.get('id')];
    return !!(studentSection && studentSection.get('publishedSnapshot'));
  }

  _ensureSubjectsLoaded() {
    let sections = this.nonHeaderSections || [];
    sections.forEach((section) => {
      // In snapshot mode the subject record's possibleGrades is no longer read
      // (chips read from snapshot.effort / progressDescriptor; strand-visual
      // renders from captured markup; progress-visual renders from captured
      // SVG). Skip the live load only for sections that carry their own
      // snapshot — a content section without one still renders judgement-label
      // against resolvedSubject.possibleGrades, so it needs the live record.
      if (this.isPublished && this._sectionHasSnapshot(section)) return;
      let parsed = this._parseConfig(section);
      let subjectId = parsed && parsed.subjectId;
      if (subjectId && !this.store.peekRecord('goals/subject', subjectId)) {
        this.store.findRecord('goals/subject', subjectId).catch(() => null);
      }
    });
  }

  _collectAssessmentModuleIds() {
    let allModuleIds = new Set();
    (this.nonHeaderSections || []).forEach((section) => {
      let parsed = this._parseConfig(section);
      (parsed.components || []).forEach((comp) => {
        if (comp.type === 'assessment-data' && comp.modules) {
          comp.modules.forEach((m) => {
            let id = typeof m === 'string' ? m : m.id;
            if (id) allModuleIds.add(id);
          });
        }
      });
    });
    return allModuleIds;
  }

  // True when at least one assessment-data component is scoped to the current
  // academic year — in that case we must fetch the full grade history (rather
  // than only the latest grade per module) so the date window can be applied.
  // Current academic year is the default, so only an explicit 'latest' opts out.
  _usesCurrentYearGrades() {
    let result = false;
    (this.nonHeaderSections || []).forEach((section) => {
      let parsed = this._parseConfig(section);
      (parsed.components || []).forEach((comp) => {
        if (comp.type === 'assessment-data' && comp.gradeScope !== 'latest') {
          result = true;
        }
      });
    });
    return result;
  }

  // Unix bounds of the current academic year, honouring the school's rollover
  // date. Null while the dates service is still loading options — the
  // assessment-groups helper then leaves the grades unfiltered.
  academicYearBounds() {
    let dates = this.dates;
    if (!dates) return null;
    let year = dates.get('currentAcademicYear');
    if (year == null || isNaN(year)) return null;
    let bounds = dates.getYearBounds(year);
    if (!bounds) return null;
    return {
      startUnix: moment(bounds.from, 'YYYY-MM-DD').startOf('day').unix(),
      endUnix: moment(bounds.to, 'YYYY-MM-DD').endOf('day').unix(),
    };
  }

  @computed('dates.{currentAcademicYear,rolloverDate}')
  get academicYearBoundsComputed() {
    return this.academicYearBounds();
  }

  @computed('academicYearBoundsComputed')
  get currentAcademicYearStartUnix() {
    let bounds = this.academicYearBoundsComputed;
    return bounds ? bounds.startUnix : null;
  }

  @computed('academicYearBoundsComputed')
  get currentAcademicYearEndUnix() {
    let bounds = this.academicYearBoundsComputed;
    return bounds ? bounds.endUnix : null;
  }

  @computed('headerSection.configuration')
  get headerComponents() {
    let header = this.headerSection;
    if (!header) return [];
    return this._parseConfig(header).components || [];
  }

  // Store direct references so chained dependent keys work for reactivity
  @computed('headerComponents.[]')
  get nameAvatarSettings() {
    return this.headerComponents.find(
      (c) => (c.type || (c.get && c.get('type'))) === 'student-name-avatar',
    );
  }

  @computed('headerComponents.[]')
  get detailComponent() {
    return this.headerComponents.find(
      (c) => (c.type || (c.get && c.get('type'))) === 'student-detail',
    );
  }

  // Bumped after we've fetched any missing peopleSchema/field records so
  // resolvedDetailFields re-runs and renders proper labels instead of UUIDs.
  @tracked _detailFieldsLoaded = 0;

  @tracked
  _lastLiveAvatarMediaId;

  @tracked
  _lastLiveLogoMediaId;

  @tracked
  _lastSnapshotAvatarMediaId;

  @tracked
  _lastSnapshotLogoMediaId;
  @tracked _snapshotAvatarHolder;
  @tracked _snapshotLogoHolder;
  @tracked _liveAvatarHolder;
  @tracked _liveLogoHolder;

  @computed(
    'detailComponent',
    'detailComponent.selectedFields.[]',
    'headerSnapshot',
    'student',
    'studentReport',
    '_detailFieldsLoaded',
  )
  get resolvedDetailFields() {
    // Snapshot path: the backend has already resolved label + display
    // value for each selected field; iterate them in order.
    let snap = this.headerSnapshot;
    if (
      snap &&
      snap.studentDetail &&
      Array.isArray(snap.studentDetail.fields)
    ) {
      return snap.studentDetail.fields;
    }

    let detailComp = this.detailComponent;
    if (!detailComp) return [];
    let fieldIds =
      detailComp.selectedFields ||
      (detailComp.get && detailComp.get('selectedFields')) ||
      [];
    let entity = this.student;
    let isPreview = !this.studentReport;
    return fieldIds
      .map((id) => {
        let field = this.store.peekRecord('people-schema/field', id);
        if (!field) return null;
        let label = field.get('label');
        let key = field.get('key');
        let fieldValue = entity && key ? entity.get(`fields.${key}`) : null;
        let value = displayFieldValue(fieldValue, field);
        if (!value && isPreview) value = 'Sample data';
        return { id, label, value };
      })
      .filter(Boolean);
  }

  async _ensureDetailFieldsLoaded() {
    // resolvedDetailFields short-circuits to snap.studentDetail.fields only
    // when the header section's snapshot actually carries them, so skip the
    // live load on exactly that condition. Gating on _hasAnySnapshot() would
    // suppress the field load for a partially-snapshotted report (header not
    // snapshotted but some content section is), leaving labels unresolved.
    let snap = this.headerSnapshot;
    if (
      this.isPublished &&
      snap &&
      snap.studentDetail &&
      Array.isArray(snap.studentDetail.fields)
    ) {
      return;
    }
    let detailComp = this.detailComponent;
    if (!detailComp) return;
    let fieldIds =
      detailComp.selectedFields ||
      (detailComp.get && detailComp.get('selectedFields')) ||
      [];
    let missing = fieldIds.filter(
      (id) => !this.store.peekRecord('people-schema/field', id),
    );
    if (!missing.length) return;
    await Promise.all(
      missing.map((id) =>
        this.store.findRecord('people-schema/field', id).catch(() => null),
      ),
    );
    if (this.isDestroyed || this.isDestroying) return;
    this._detailFieldsLoaded = this._detailFieldsLoaded + 1;
  }

  // The student's name as this report shows it, per the template's
  // student name formats. Shared with the progress visual so the whole report
  // names the student the same way.
  @computed(
    'reportTemplate.{studentFirstNameFormat,studentLastNameFormat}',
    'nameAvatarSettings.{firstNameKey,lastNameKey}',
    'student.{firstName,lastName,legalFirstName,legalLastName}',
  )
  get resolvedStudentName() {
    return reportStudentName(
      this.student,
      this.reportTemplate,
      this.nameAvatarSettings,
    );
  }

  @computed('headerSnapshot', 'resolvedStudentName.fullName', 'student')
  get previewStudentName() {
    // Snapshot path: backend has resolved firstName + lastName from the
    // template's student name formats.
    let snap = this.headerSnapshot;
    if (snap && snap.studentNameAvatar) {
      let first = snap.studentNameAvatar.firstName || '';
      let last = snap.studentNameAvatar.lastName || '';
      let name = `${first} ${last}`.trim();
      if (name) return name;
    }

    if (!this.student) return 'Student';
    return this.resolvedStudentName.fullName || 'Student';
  }

  @computed('previewStudentName')
  get previewStudentInitials() {
    let parts = this.previewStudentName.split(' ');
    return parts
      .map((p) => p.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
  }

  // The header renders this verbatim — the template no longer prepends a
  // literal "Year ", because schools that define year-level options carry the
  // word "Year" inside the option label already (e.g. "Year 3", "Kindergarten"),
  // and prefixing produced "Year Year 3". So this returns the fully-formatted
  // label, mirroring person.ribbonText (prefix "Year " only when the school has
  // no option list).
  @computed(
    'headerSnapshot',
    'student.{ribbonText,currentYearLevel,yearLevelOptions}',
    'studentReport',
  )
  get previewYearLevel() {
    // Snapshot path: the published renderer can't load yearLevelOptions, so
    // it formats whatever the snapshot froze. Older / non-option snapshots
    // store a bare number ("4") → prefix "Year "; option-label snapshots
    // store the full label ("Year 3", "Kindergarten") → render as-is. (Once
    // the producer stores the formatted label for every school — see
    // publish-snapshot.md — this collapses to "render verbatim".)
    let snap = this.headerSnapshot;
    if (
      snap &&
      snap.studentNameAvatar &&
      !isEmpty(snap.studentNameAvatar.yearLevel)
    ) {
      let snapYear = String(snap.studentNameAvatar.yearLevel).trim();
      return /^\d+$/.test(snapYear) ? `Year ${snapYear}` : snapYear;
    }
    // Live path: ribbonText is the canonical year-level display string and
    // already handles the per-school option labels.
    let year = this.student && this.student.get('ribbonText');
    if (!isEmpty(year)) return year;
    // ribbonText uses a truthy guard, so a valid year level of 0 comes back
    // as ''. Reconstruct that case here (mirroring ribbonText's formatting)
    // so a year-0 student isn't dropped — Year 0 is a valid level.
    let level = this.student && this.student.get('currentYearLevel');
    if (!isEmpty(level)) {
      let options = this.student.get('yearLevelOptions');
      return (isEmpty(options) ? 'Year ' : '') + level;
    }
    // Builder/preview mode (no real student) — show a placeholder.
    return this.studentReport ? '' : 'Year 1';
  }

  // student.homeroom is derived from learner.homeroomId via a store
  // lookup, which the sample student doesn't have. Fall back to a
  // placeholder label in builder/preview mode only.
  @computed('headerSnapshot', 'student.homeroom.label', 'studentReport')
  get previewHomeroom() {
    let snap = this.headerSnapshot;
    if (snap && snap.studentNameAvatar && snap.studentNameAvatar.homeroom) {
      return snap.studentNameAvatar.homeroom;
    }
    let label = this.student && this.student.get('homeroom.label');
    if (label) return label;
    return this.studentReport ? '' : 'Room 1';
  }

  // Comma-separated list of the homeroom's current staff. Falls back to a
  // sample value in builder/preview mode so the toggle has visible effect.
  @computed('headerSnapshot', 'student.homeroom.staffNames', 'studentReport')
  get previewClassTeachers() {
    let snap = this.headerSnapshot;
    if (
      snap &&
      snap.studentNameAvatar &&
      snap.studentNameAvatar.classTeachers
    ) {
      return snap.studentNameAvatar.classTeachers;
    }
    let names = this.student && this.student.get('homeroom.staffNames');
    if (names) return names;
    return this.studentReport ? '' : 'Ms Sample Teacher';
  }

  // School name for the header. Snapshot path reads from schoolNameLogo.name;
  // live path falls back to schoolRecord.name. Logo src is handled separately
  // by previewLogoSrc, which routes the snapshot logoMediaId through the media
  // service via _snapshotLogoHolder.
  @computed('headerSnapshot', 'schoolRecord.name')
  get previewSchoolName() {
    let snap = this.headerSnapshot;
    if (snap && snap.schoolNameLogo && snap.schoolNameLogo.name) {
      return snap.schoolNameLogo.name;
    }
    return this.get('schoolRecord.name');
  }

  // Avatar image src for the header. Snapshot path reads the resolved blob
  // URL off the synthetic holder populated from
  // snapshot.studentNameAvatar.avatarMediaId; live path reads the resolved
  // blob URL off the synthetic live holder populated from student.mediaId
  // (see _loadHeaderMedia -- student.image doesn't exist on the model
  // anymore, that resolution now lives here or in AvatarImage).
  @computed(
    'headerSnapshot.studentNameAvatar.avatarMediaId',
    '_snapshotAvatarHolder.image',
    '_liveAvatarHolder.image',
  )
  get previewAvatarSrc() {
    let snap = this.headerSnapshot;
    if (snap && snap.studentNameAvatar) {
      // Snapshot path: commit to snapshot data — don't load the live student record.
      // avatarMediaId null means no avatar at publish time; return null so the
      // template shows initials instead of a post-publish upload.
      if (snap.studentNameAvatar.avatarMediaId) {
        return this.get('_snapshotAvatarHolder.image');
      }
      return null;
    }
    return this.get('_liveAvatarHolder.image');
  }

  // School logo src for the header. Same pattern as previewAvatarSrc.
  @computed(
    'headerSnapshot.schoolNameLogo.logoMediaId',
    '_snapshotLogoHolder.logo',
    '_liveLogoHolder.logo',
  )
  get previewLogoSrc() {
    let snap = this.headerSnapshot;
    if (snap && snap.schoolNameLogo) {
      // Snapshot path: logoMediaId null means no logo at publish time; return null
      // so the template shows the school icon rather than a post-publish upload.
      if (snap.schoolNameLogo.logoMediaId) {
        return this.get('_snapshotLogoHolder.logo');
      }
      return null;
    }
    return this.get('_liveLogoHolder.logo');
  }
}
